import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import { UserCheck, Eye, Trash2, RefreshCw, History, XCircle, Search, AlertOctagon, ChevronRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import HistoriqueModal from '../components/HistoriqueModal';
import ImportantNotesButton from '../components/ImportantNotesButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LIVRAISON_LABELS = {
    en_attente: { label: 'En attente', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    sans_livraison: { label: 'Sans livraison', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
    programme: { label: 'Programmee', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const ACOMPTE_LABELS = {
    non_verse: { label: 'Non verse', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    verse: { label: 'Versee', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const STATUT_LABELS = {
    en_attente: { label: 'En attente', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    programme: { label: 'Programmee', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    stocke: { label: 'Stocke', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
    termine: { label: 'Terminee', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [litigesCount, setLitigesCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [historyTarget, setHistoryTarget] = useState(null);
    // Filters
    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');
    const [livraisonFilter, setLivraisonFilter] = useState('all');
    const [acompteFilter, setAcompteFilter] = useState('all');
    const navigate = useNavigate();
    const { canDelete } = useCan('clients');

    const fetchClients = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
            setClients(res.data);
        } catch {
            toast.error('Erreur lors du chargement des clients');
        } finally {
            setLoading(false);
        }
    };

    const fetchLitigesCount = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/litiges`, { withCredentials: true });
            setLitigesCount((res.data || []).length);
        } catch { /* silent */ }
    };

    useEffect(() => {
        fetchClients();
        fetchLitigesCount();
    }, []);

    const handleDelete = async (clientId) => {
        if (!window.confirm('Supprimer ce client ?')) return;
        try {
            await axios.delete(`${API_URL}/api/clients/${clientId}`, { withCredentials: true });
            toast.success('Client supprime');
            fetchClients();
        } catch {
            toast.error('Erreur');
        }
    };

    const formatDate = (d) => {
        if (!d) return '-';
        try {
            const date = new Date(d);
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch { return d; }
    };

    // Apply filters + search
    const filteredClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        return clients.filter((c) => {
            if (q) {
                const hay = [
                    c.nom, c.telephone, c.email, c.prestation,
                    c.adresse_depart, c.adresse_arrivee,
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (statutFilter !== 'all') {
                if (statutFilter === 'cancelled') {
                    if (!c.cancelled) return false;
                } else if ((c.statut || 'en_attente') !== statutFilter || c.cancelled) {
                    return false;
                }
            }
            if (livraisonFilter !== 'all' && (c.livraison || 'en_attente') !== livraisonFilter) {
                return false;
            }
            if (acompteFilter !== 'all' && (c.acompte || 'non_verse') !== acompteFilter) {
                return false;
            }
            return true;
        });
    }, [clients, search, statutFilter, livraisonFilter, acompteFilter]);

    return (
        <Layout>
            <div className="space-y-6" data-testid="clients-page">
                <div className="flex items-center gap-3">
                    <UserCheck className="w-6 h-6 text-[#1B365D]" />
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Liste des clients ({filteredClients.length}{filteredClients.length !== clients.length ? ` / ${clients.length}` : ''})
                    </h1>
                </div>

                {/* Litige shortcut block */}
                <button
                    type="button"
                    onClick={() => navigate('/clients/litiges')}
                    className="w-full text-left bg-gradient-to-r from-red-50 via-white to-red-50 border border-red-200 rounded-xl shadow-sm hover:shadow-md hover:border-red-300 transition-all p-4 flex items-center gap-4 group"
                    data-testid="clients-litige-block"
                >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm shrink-0">
                        <AlertOctagon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Litiges
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Suivi des litiges clients (réclamations, remboursements, contestations)
                        </p>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                        <span
                            className="text-2xl font-bold text-red-600 leading-none"
                            data-testid="litiges-count-badge"
                        >
                            {litigesCount}
                        </span>
                        <span className="text-[11px] uppercase tracking-wide text-red-500/80 mt-0.5">
                            {litigesCount > 1 ? 'dossiers' : 'dossier'}
                        </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors shrink-0" />
                </button>

                {/* Search + filters */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-col lg:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Rechercher nom, téléphone, email, adresse…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid="clients-search-input"
                        />
                    </div>
                    <Select value={statutFilter} onValueChange={setStatutFilter}>
                        <SelectTrigger className="w-full lg:w-48" data-testid="clients-statut-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="programme">Programmée</SelectItem>
                            <SelectItem value="stocke">Stocké</SelectItem>
                            <SelectItem value="termine">Terminée</SelectItem>
                            <SelectItem value="cancelled">Annulée</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={livraisonFilter} onValueChange={setLivraisonFilter}>
                        <SelectTrigger className="w-full lg:w-48" data-testid="clients-livraison-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes livraisons</SelectItem>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="sans_livraison">Sans livraison</SelectItem>
                            <SelectItem value="programme">Programmée</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={acompteFilter} onValueChange={setAcompteFilter}>
                        <SelectTrigger className="w-full lg:w-48" data-testid="clients-acompte-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous acomptes</SelectItem>
                            <SelectItem value="non_verse">Non versé</SelectItem>
                            <SelectItem value="verse">Versé</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div className="p-12 text-center">
                            <UserCheck className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-400 text-sm">
                                {clients.length === 0
                                    ? 'Aucun client pour le moment'
                                    : 'Aucun client ne correspond à vos filtres'}
                            </p>
                            {clients.length === 0 && (
                                <p className="text-gray-300 text-xs mt-1">Les clients sont crees automatiquement lorsqu'un devis est accepte</p>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="clients-table">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Client</th>
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Prestation</th>
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Date dem</th>
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Livraison</th>
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Acompte</th>
                                        <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500">Statut</th>
                                        <th className="text-right px-5 py-3.5 text-sm font-semibold text-gray-500">Actes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...filteredClients].sort((a, b) => (b.acompte_urgent ? 1 : 0) - (a.acompte_urgent ? 1 : 0)).map(client => {
                                        const liv = LIVRAISON_LABELS[client.livraison] || LIVRAISON_LABELS.en_attente;
                                        const acomp = ACOMPTE_LABELS[client.acompte] || ACOMPTE_LABELS.non_verse;
                                        const stat = STATUT_LABELS[client.statut] || STATUT_LABELS.en_attente;
                                        const cancelled = !!client.cancelled;
                                        return (
                                            <tr key={client.id}
                                                className={`border-b transition-colors cursor-pointer ${cancelled ? 'bg-red-50/60 hover:bg-red-50 border-red-100' : 'border-gray-50 hover:bg-gray-50/50'}`}
                                                data-testid={`client-row-${client.id}`}
                                                onClick={() => navigate(`/clients/${client.id}`)}>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${cancelled ? 'bg-red-100' : 'bg-blue-100'}`}>
                                                            <UserCheck className={`w-4 h-4 ${cancelled ? 'text-red-600' : 'text-blue-600'}`} />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className={`font-semibold text-sm ${cancelled ? 'text-red-700 line-through' : 'text-gray-800'} inline-flex items-center gap-2`}>
                                                                {client.nom}
                                                                <ImportantNotesButton
                                                                    prospectId={client.prospect_id}
                                                                    prospectName={client.nom}
                                                                    variant="icon-only"
                                                                />
                                                            </span>
                                                            {cancelled && client.cancel_raison && (
                                                                <span className="text-[11px] text-red-600 italic mt-0.5 max-w-[280px] truncate" title={client.cancel_raison}>
                                                                    Annulée : {client.cancel_raison}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Badge className={`text-xs border ${cancelled ? 'bg-gray-100 text-gray-500 border-gray-200 line-through' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                                        {client.prestation || '-'}
                                                    </Badge>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className={`text-sm ${cancelled ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{formatDate(client.date_demenagement)}</div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    {cancelled ? (
                                                        <Badge className="text-xs border bg-red-100 text-red-700 border-red-300">Annulée</Badge>
                                                    ) : (
                                                        <Badge className={`text-xs border ${liv.cls}`}>{liv.label}</Badge>
                                                    )}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <Badge className={`text-xs border ${acomp.cls} ${cancelled ? 'opacity-60' : ''}`}>{acomp.label}</Badge>
                                                        {client.acompte_urgent && !cancelled && (
                                                            <Badge className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 font-bold animate-pulse">URGENT</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    {cancelled ? (
                                                        <Badge className="text-xs border bg-red-100 text-red-700 border-red-300 font-semibold">
                                                            <XCircle className="w-3 h-3 mr-1 inline-block" /> Annulée
                                                        </Badge>
                                                    ) : (
                                                        <Badge className={`text-xs border ${stat.cls}`}>{stat.label}</Badge>
                                                    )}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button onClick={(e) => { e.stopPropagation(); setHistoryTarget({ prospectId: client.prospect_id, nom: client.nom }); }}
                                                            className="p-1.5 rounded-lg hover:bg-[#C9A962]/10 text-gray-400 hover:text-[#C9A962] transition-colors"
                                                            data-testid={`history-client-${client.id}`}
                                                            title="Historique">
                                                            <History className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.id}`); }}
                                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                                            data-testid={`view-client-${client.id}`}>
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={(e) => { e.stopPropagation(); handleDelete(client.id); }}
                                                            disabled={!canDelete}
                                                            title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                                            data-testid={`delete-client-${client.id}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <HistoriqueModal
                open={!!historyTarget}
                onOpenChange={(o) => !o && setHistoryTarget(null)}
                prospectId={historyTarget?.prospectId}
                title={historyTarget?.nom}
            />
        </Layout>
    );
}
